import { ImageProps, Locale } from '@/root/shared-types'
import { SailChannel } from '@/root/shared-types/dto/SailPricing'

export interface Attribute {
  attribute: {
    code: string
  }
  structure: {
    value: AttributeStructure
  }
}

type AttributeStructure =
  | ({ __type: 'file' } & { value: string; fileName: string })
  | ({ __type: 'string' } & { value: string })

export interface Guest {
  birthday: string
  firstName: string
  lastName: string
  ageInYears: number
  attributes: Attribute[]
  fullName: string
  seqN: number
  birthPlace: string
  travelDocument: {
    number: string
    expireDate: string
  }
  backupRegistryUsed?: boolean
  citizenship?: string
  personalIdentificationNumber?: string
  gender?: 'M' | 'F'
  comments?: string
}

export interface Reservation {
  reservationId: number
  status: BookingStatus
  paymentInfo: PaymentInfo
  routes: string[]
  saleChannel: SailChannel
  sailPackages?: Array<SailPackage>
  token?: string
  guests?: Guest[]
  items?: ReservationItem[]
  terms?: Terms[]
  reservationOwner?: ReservationOwner
  vehicles?: Vehicle[]
  arrivalAt?: string
  departureAt?: string
  allowance?: {
    cancel: boolean
    edit: boolean
  }
  links?: Record<string, string>
  expireAt?: string
  tempExpireAt?: string
  createdAt?: string
  createdOnDate?: string
  validationResults?: unknown[]
}

export interface PaymentTransaction {
  amount: number
  amountFormatted: string
  invoiceNumber?: string
  paymentMethod: string
}

export interface PaymentInfo {
  totalPrice: TotalPrice
  toPayAmountFormatted: string
  paidAmountFormatted?: string
  paidAmount?: number
  payAtPort?: boolean
  payable?: boolean
  refundable?: boolean
  toPayAmount?: number
  toRefundAmount?: number
  refundOffers?: RefundOffer[]
  paymentTransactions?: PaymentTransaction[]
}

export interface TotalPrice {
  amount: number
  currency: string
  feeAmount: number
  amountFormatted: string
  amountWithoutTaxes: number
  feeAmountWithoutTaxes: number
  feeTaxAmount: number
  tax: number
  taxAmount: number
  formatPattern: string
}

/** A vehicle's plate and dimensions as the reservation request sends them. */
export type VehicleDetailsState = Pick<
  Vehicle,
  'licencePlate' | 'lengthInCm' | 'widthInCm' | 'heightInCm' | 'weightInKg'
>

export interface Vehicle {
  licencePlate: string
  providedPriceCategory: string
  seqN: number
  heightInCm: number
  lengthInCm: number
  weightInKg: number
  widthInCm: number
  guestSeqN: number
  attributes?: Attribute[]
  type?: 'BICYCLE' | 'CAR' | 'MOTORCYCLE' | 'TRAILER'
  registryCategory?: string
  registrationCountry?: string
  curbWeightInKg?: number
  weightOnSails?: Array<{ sailRefId: number; weightInKg: number }>
  handicapped?: boolean
  companyRegistrationNumber?: string
  backupBusinessRegistryUsed?: boolean
  backupRoadAdministrationRegistryUsed?: boolean
}

export interface PortTitles {
  code: string
  titles: LocalizedLocation
}
export interface SailPackage {
  code: string
  roundtrip: boolean
  sailRefs: SailRef[]
  title: string
  seqN?: number
  taxi?: boolean
  titles?: LocalizedLocation
  refundOfferId?: string
  route?: SailPackageRoute | string
  portFrom?: PortTitles
  backwardSailPackage?: string
  fullday?: boolean
  routeAttributes?: Record<string, boolean | string>
}

export interface SailPackageRoute {
  code: string
  from: LocalizedLocation
  to: LocalizedLocation
  type: SailType
  attributes: { attribute: { code: AttributeCodeType }; structure: { value: string | number | boolean } }[]
}

interface Features {
  supportCars: boolean
  supportPassengers: boolean
  supportTrucks: boolean
  supportVehicles: boolean
  showProducts: boolean
  supportBicycles?: boolean // TODO: Add feature in BE and remove '?'
}

export interface SailPackagesGroup<T = SailPackage[]> {
  code: string
  from: LocalizedLocation
  to: LocalizedLocation
  taxi: boolean
  title: string
  features: Features
  sailPackages: T
}

enum AttributeCode {
  SUPPORT_PASSENGERS = 'SUPPORT_PASSENGERS',
  SUPPORT_TRUCKS = 'SUPPORT_TRUCKS',
  SUPPORT_CARS = 'SUPPORT_CARS',
  SHOW_PRODUCTS = 'SHOW_PRODUCTS',
}

export type AttributeCodeType = keyof typeof AttributeCode

enum SailOption {
  ROUNDTRIP_TAXI = 'ROUNDTRIP_TAXI',
  CIRCLE = 'CIRCLE',
  DIRECT = 'DIRECT',
}

type SailType = keyof typeof SailOption

export type LocalizedLocation = Partial<Record<Locale, string>>

interface Port {
  code: string
}

export interface SailRef {
  allowance: {
    cancel: boolean
    edit: boolean
  }
  arrivalAt: string
  arrivalTo: string
  departureAt: string
  departureDate: string
  departureFrom: string
  departureTime: string
  expired: boolean
  portFrom: Port
  portTo: Port
  route: string
  routeLeg: string
  routeLegTitle: string
  sailRefId: number
  status: 'PENDING' | 'OPEN' | 'CHECK-IN' | 'CLOSED' | 'TRAVELLED' | 'CANCELLED'
  vessel: string
  vesselTitle: string
  cancelledAt?: string
  expireCancelledSailsInXDays?: number
}

export interface CompanyInfo {
  legalName?: string
  address?: string
  registrationNumber?: string
  city?: string
  zip?: string
  country?: string
  monthlyCreditLimit?: number
}

export interface ReservationOwner {
  email?: string
  firstName?: string
  fullName?: string
  lastName?: string
  phones?: PhoneNumber[]
  phoneNumber?: string
  companyId?: number
  companyInfo?: CompanyInfo
}

interface PhoneNumber {
  intlCode: string
  phoneNumber: string
  phoneNumberSuffix?: string
  primary?: boolean
}

export interface Terms {
  additionalTicketTerms: string
  language: Locale
  ticketTerms: string
}

export interface ReservationItem {
  internal: boolean
  inventoryClass: string
  ownerSeqNs: number[]
  price: number
  priceCategory: string // todo change it to PriceCategory and fix TS errors
  priceCategorySubType: PriceCategorySubType
  priceCategoryTranslation: string
  pricePerUnit: number
  pricePerUnitWithoutTax: number
  priceWithoutTax: number
  quantity: number
  resident: boolean
  sailRefIdToPricePerSail: {
    [key: string]: number
  }
  tax: number
  taxAmount: number
  type: string // TODO: add ReservationItemType union ('PASSENGER' | 'VEHICLE' | 'FEE' | 'CABIN' | 'ADDON') and fix TS errors
  quantityDecimal: boolean
  saleChannel?: 'ONLINE'
  sailPackageSeqN?: number
  promotion?: string
  /** Elevated-tariff condition codes the sailing applied to this item. Texts live in route attributes. */
  coefficients?: string[]
  responsibleGuestSeqN?: number
}

export interface Sails {
  totalRefundAmount: number
  refundOffer: RefundOffer
}

interface RefundOffer {
  sails: RefundSails[]
  totalRefundAmount: number
  refundOfferId?: string
}

interface RefundSails {
  amount: number
  percent: number
  routeLeg: string
}

export type BookingStatus = 'OK' | 'CN' | 'BOOKED' | 'DL' | 'OFFER' | 'TEMP'

export interface TicketPartProps {
  numOfPassengers: number
  vesselTitle: string
  arrivalPort: string
  departurePort: string
  numOfCars: number
  sailRefId: number
  items: ReservationItem[]
  isRefund: boolean
  vehicles?: Vehicle[]
  departure?: string
  arrival?: string
  numOfCycles?: number
  isCancelled?: boolean
  residents?: Guest[]
  cancellationInfo?: CancellationInfo
  allowance?: { cancel: boolean; edit: boolean }
}

export interface CancellationInfo {
  expired: boolean
  sailRefId: number
  cancelledAt?: string
  expireCancelledSailsInXDays?: number
}

export interface CustomerInfoProps {
  email: string
  fullName: string
  firstName: string
  lastName: string
  phoneNumber: string
  companyInfo?: CompanyInfo
}

export interface TicketData {
  there: TicketPartProps
  back: TicketPartProps
  customerInfo: CustomerInfoProps
  reservationId: number
  isRoundTrip: boolean
  status: BookingStatus
  terms: Terms[]
  paymentInfo: PaymentInfo
  currency: string
  amount: number
  totalToPay: string
  feeAmount: number
  route: string
  charges: {
    quantity: number
    price: number
    vat: number
    subtotal: number
    tax?: number
  }
  token?: string
  expireAt?: string
}
export interface TicketProps {
  ticketData: TicketData
  confirmationTerms?: ConfirmationTermsProps
  hideQrCode?: boolean
  isMacDevice?: boolean
  privatePersonForm?: boolean
  sailPackages?: SailPackage[]
}

interface Localization {
  id: number
  locale: string
}

export interface ConfirmationTerm {
  id: number
  sailpackage: string
  body: string
  locale: string
  created_at: string
  updated_at: string
  localizations: Localization[]
}
export interface ConfirmationTermsProps {
  confirmationTerms: ConfirmationTerm[]
}

export type PriceCategory = 'ADDON' | 'CAR_DECK' | 'CABIN/DECK' | 'CARGO' | 'FEE'

export type PriceCategorySubType =
  | 'BAGGAGE'
  | 'BANK_FEE'
  | 'BAR'
  | 'BERTH'
  | 'BICYCLE'
  | 'CABIN'
  | 'CARGO'
  | 'DECK'
  | 'DEFAULT'
  | 'FEE'
  | 'FIXED_FEE'
  | 'GOODS_CARRIAGE'
  | 'TRAILER'
  | 'VEHICLE'

export type AttributeGroup =
  | 'ADDON'
  | 'ACCOUNT'
  | 'BUSINESS_RULE'
  | 'SALE_CHANNEL'
  | 'SALE_CHANNEL_FALLBACK'
  | 'RESERVATION'
  | 'CUSTOMER'
  | 'VESSEL'
  | 'PRICE_CATEGORY'
  | 'PROMOTION'
  | 'CABIN'
  | 'CABIN_CATEGORY'
  | 'ROUTE'
  | 'ROUTE_ATTRIBUTE'
  | 'ROUTE_ATTRIBUTE_ATTR'
  | 'KIOSK'
  | 'CASH_DESK'
  | 'AGENCY'
  | 'ROLE'
  | 'RESERVATION_VEHICLE'
  | 'RESERVATION_GUEST'

export type AttributeValueStructure = 'SINGLE' | 'SET' | 'COMPLEX'

export type RefundOfferStatus =
  | 'WAITING_FOR_APPROVAL'
  | 'APPROVED'
  | 'OFFER'
  | 'PENDING'
  | 'SENT'
  | 'REFUNDED'
  | 'PART_REFUNDED'
  | 'NOT_REFUNDED'
  | 'REJECTED'
  | 'FAILED'

export type SailActivity = 'DEPARTURE' | 'ARRIVAL' | 'VESSEL OVERNIGHT' | 'EMPTY LEG' | 'ONBOARD EVENT'

export type TravelClass = 'BERTH' | 'CABIN' | 'OTHER' | 'SEAT'

export type MontonioPaymentCode = 'COOP' | 'LHV' | 'LUMINOR' | 'SEB' | 'SWEDBANK' | 'CARD' | 'BOOKED' | 'NONE'

export const PAYMENT_METHODS = {
  CARD: 'CARD',
  CREDIT: 'CREDIT',
} as const

export interface MontonioPaymentMethod {
  code: MontonioPaymentCode
  country: 'EE'
  creditClient: boolean
  enabled: boolean
  immediatePayment: boolean
  // f-edge leaves a null logoUrl out of the JSON (the BOOKED method has none)
  logoUrl?: string
  partialPayment: boolean
  paymentProvider: 'MONTONIO'
  paymentProviderCode: string
  pingable: boolean
  privateClient: boolean
  redirectToPaymentProvider: boolean
  saleChannel: 'ONLINE'
  seqN: number
  title: string
  transactionCheckAvailable: boolean
  transactionLogAvailable: boolean
  voucherPayment: boolean
}

export interface CreditPaymentMethod {
  code: 'CREDIT'
  country: string
  creditClient: boolean
  enabled: boolean
  immediatePayment: boolean
  partialPayment: boolean
  pingable: boolean
  privateClient: boolean
  redirectToPaymentProvider: boolean
  saleChannel: string
  seqN: number
  title: string
  transactionCheckAvailable: boolean
  transactionLogAvailable: boolean
  voucherPayment: boolean
}

export type PaymentMethod = MontonioPaymentMethod | CreditPaymentMethod
